import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Paper } from "@mui/material";
import { Stepper } from "@outpave/ds";
import { Center } from "components/layout/reusableLayouts";
import BusinessInformation from "components/onboarding/BusinessInformation";
import PrimaryAuthorizedPerson from "components/onboarding/PrimaryAuthorizedPerson";
import Owners from "components/onboarding/Owners";
import ReviewAndSubmit from "components/onboarding/Review";
import { CompanyProfileTabProps } from "hooks/useCompanySettings";
import { User } from "hooks/useAddUpdateUser";
import { FormikAddress } from "utils/addressFormat";
import { onboardingRail } from "utils/onboarding/onboardingSteps";
import { mergeUserProfiles } from "utils/onboarding/onboardingProfiles";

export type OnboardingAddress = FormikAddress & { id?: string };

export type OnboardingInfo = {
  companyProfile?: CompanyProfileTabProps;
  addressProfile?: OnboardingAddress;
  userProfiles?: User[];
};

export type OnboardingUpdate = Omit<OnboardingInfo, "userProfiles"> & {
  userProfiles?: User | User[];
};

export type OnboardingProps = {
  onboardingInfo: OnboardingInfo | null;
  handleNext: (update: OnboardingUpdate) => void;
  handleBack: (update: OnboardingUpdate) => void;
  handleSetOnboardingInfo: (update: OnboardingUpdate) => void;
  ownersRequired?: boolean;
  setOwnersRequired?: React.Dispatch<React.SetStateAction<boolean>>;
};

const mergeOnboardingInfo = (
  prev: OnboardingInfo | null,
  update: OnboardingUpdate,
): OnboardingInfo => ({
  ...prev,
  ...(update.companyProfile && { companyProfile: update.companyProfile }),
  ...(update.addressProfile && { addressProfile: update.addressProfile }),
  ...(update.userProfiles && {
    userProfiles: mergeUserProfiles(
      prev?.userProfiles ?? [],
      update.userProfiles,
    ),
  }),
});

const OnboardCompany: React.FC = () => {
  const { t } = useTranslation();
  const [onboardingInfo, setOnboardingInfo] = useState<OnboardingInfo | null>(
    null,
  );
  const [step, setStep] = useState(0);
  const [ownersRequired, setOwnersRequired] = useState(false);
  const { stepKeys, activeStep } = onboardingRail(step, ownersRequired);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [step]);

  // Functional, because steps call this after an await, from a closure that
  // holds the info as it was when they rendered.
  const handleSetOnboardingInfo = (update: OnboardingUpdate) => {
    setOnboardingInfo((prev) => mergeOnboardingInfo(prev, update));
  };

  const handleNext = (update: OnboardingUpdate) => {
    handleSetOnboardingInfo(update);
    setStep((prev) => (prev === 1 && !ownersRequired ? 3 : prev + 1));
  };

  const handleBack = (update: OnboardingUpdate) => {
    handleSetOnboardingInfo(update);
    setStep((prev) => (prev === 3 && !ownersRequired ? 1 : prev - 1));
  };

  return (
    <Center
      maxWidth={600}
      sx={{ gap: "var(--space-7)", paddingBottom: "var(--space-9)" }}
    >
      <Stepper
        activeStep={activeStep}
        steps={stepKeys.map((key) => ({ id: key, label: t(key) }))}
      />
      <Paper sx={{ padding: "var(--space-9)" }}>
        {step === 0 && (
          <BusinessInformation
            onboardingInfo={onboardingInfo}
            handleNext={handleNext}
            handleBack={handleBack}
            handleSetOnboardingInfo={handleSetOnboardingInfo}
          />
        )}
        {step === 1 && (
          <PrimaryAuthorizedPerson
            onboardingInfo={onboardingInfo}
            handleNext={handleNext}
            handleBack={handleBack}
            handleSetOnboardingInfo={handleSetOnboardingInfo}
            ownersRequired={ownersRequired}
            setOwnersRequired={setOwnersRequired}
          />
        )}
        {step === 2 && (
          <Owners
            onboardingInfo={onboardingInfo}
            handleNext={handleNext}
            handleBack={handleBack}
            handleSetOnboardingInfo={handleSetOnboardingInfo}
          />
        )}
        {step === 3 && (
          <ReviewAndSubmit
            onboardingInfo={onboardingInfo}
            ownersRequired={ownersRequired}
            handlePageSelect={setStep}
          />
        )}
      </Paper>
    </Center>
  );
};

export default OnboardCompany;
